import { Box } from '@mui/system';
import { useNotification } from '@revenuewell/front-end-bundle';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { Communication, ContactApi } from '../../../../../../services/api-client/contact-api';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { sortByDateAndStatus } from '../../../../../shared/scheduled-comms/utils/sort-comms';
import { PatientProfileHeader } from '../../header/header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { DotSeparatedTitle, LiSeparator, List, ListItem } from '../../patient-profile-widget.styles';
import { ViewContent } from '../shared/view-content/view-content';
import { ScheduledCommsSkeleton } from './scheduled-comms.skeleton';
import { getCommIcon } from './utils/get-comm-icon';
import { getCommStatus } from './utils/get-comm-status';

export const ScheduledCommsView: FC = () => {
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [comms, setComms] = useState<Communication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  useEffect(() => {
    loadScheduledCommunications();
  }, []);

  const loadScheduledCommunications = useCallback(async () => {
    setIsLoading(true);

    try {
      const communications = await contactApi.getScheduledCommunications(contactDetails.id, locationId);

      setComms(sortByDateAndStatus(communications));
    } catch {
      showError('Unable to load scheduled communications.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  return (
    <>
      <PatientProfileHeader />
      <QuickNavMenu />
      <ViewContent>
        {isLoading && <ScheduledCommsSkeleton />}
        {!isLoading && comms.length === 0 && <NoDataComponent />}
        {!isLoading && comms.length > 0 && (
          <Scrollbars autoHide style={{ flexGrow: 1 }}>
            <List>
              {comms.map((comm, index) => (
                <>
                  <ListItem key={index} icon={getCommIcon(comm)}>
                    <DotSeparatedTitle
                      leftTitle={comm.date ? moment(comm.date).format('MM/DD/YYYY') : null}
                      rightTitle={getCommStatus(comm)}
                    />
                    <Box sx={{ paddingTop: '8px', fontSize: '14px' }}>{comm.note}</Box>
                  </ListItem>
                  {index !== comms.length - 1 && <LiSeparator key={1000 + index} />}
                </>
              ))}
            </List>
          </Scrollbars>
        )}
      </ViewContent>
    </>
  );
};
